// O PROGRESSO DA GERAÇÃO — e o achado que reescreveu este plano.
//
// ── A BARRA JÁ EXISTIA QUANDO ELE RECLAMOU DE NÃO TER BARRA ─────────────────────────────────
//
// `T2.86`, na letra dele: *"agora ele tá aqui **desenhando um de um** e ele não dá mensagem de
// erro, como se ele de fato tivesse compondo."*
//
// "Desenhando um de um" **é o texto da tela**. Estava em `roteirizar.tsx`, no rótulo do botão:
// `Desenhando ${progresso.feitos + 1} de ${progresso.total}…`. O commit que o criou é `27746d1`,
// de **2026-09-09** — um dia ANTES da gravação. Confirmado por `git log -S "progresso.feitos"`.
//
// Então este plano não é "acrescentar sinal de progresso". É: **o sinal que existia não respondeu
// à pergunta que ele tinha.** E a leitura do dossiê mostrou quais eram as perguntas — duas, e não
// uma:
//
//   1. "a gente não sabe se **tá gerando de fato**" (T2.87)
//   2. "já tem uma página aqui gerada abaixo, **não sei se ela vai ser incrementada e alterada**"
//      (T2.86, segunda metade)
//
// Um contador de itens não responde nenhuma das duas. Com total = 1 — que era o caso dele —, ele
// nasce parado em "1 de 1" e morre parado. Tudo que comunica é QUANTOS; nada do que comunica é
// AINDA ESTÁ ACONTECENDO, e nada diz se o que está na tela é o resultado final.
//
// E é por isso que `T2.88` pede **porcentagem** e não contador: com um item só, só um número
// dentro do item diz alguma coisa.
//
// ── A DÚVIDA D6, E POR QUE ELA NÃO TRAVA ESTA TELA ─────────────────────────────────────────
//
// As duas gravações se invertem: *"vocês **estão** conseguindo ter essa visão de forma
// determinística"* contra *"vocês **não estão**"*. Com "estão", ele pede que a barra use o dado
// real que o time já tem; com "não estão", ele aceita estimativa justamente porque o dado real
// não existe.
//
// **Decidido em 2026-09-12, com a autonomia que o operador deu:** usar o dado REAL onde ele
// existe e ESTIMAR onde não existe, dizendo qual é qual. Isso satisfaz as duas leituras ao mesmo
// tempo, e é literalmente o que o T2.88 autoriza — *"seja por estimativa, seja por algum dado que
// foi puxado"*.
//
// O que é real: qual quadro está sendo desenhado, e quantos terminaram. A geração é em SÉRIE
// (`roteirizar.tsx:21` registra por quê: em paralelo, uma sala com trinta alunos derrubaria a
// cota da escola em minutos), então o front sabe exatamente onde está.
//
// O que é estimado: o avanço DENTRO de um quadro. Nenhum provedor devolve isso. A estimativa vem
// do tempo decorrido contra a média medida — 4,6 a 5,2 segundos por imagem, três pedidos seguidos
// no journal de produção em 2026-09-12, com o `FLUX.1-schnell` pelo HuggingFace.
//
// E a tela DIZ que aquela parte é estimativa. Uma barra que mente sobre a própria precisão é pior
// que barra nenhuma: ela treina a pessoa a não confiar no que a tela diz.

import { useEffect, useRef, useState } from "react";

import "./progresso.scss";

/** O estado de UM quadro. É isto que responde "o que estou vendo é o final?" (T2.86). */
export type EstadoDoQuadro = "esperando" | "desenhando" | "pronto" | "falhou";

export const ROTULO_DO_QUADRO: Record<EstadoDoQuadro, string> = {
  esperando: "na fila",
  desenhando: "desenhando agora",
  pronto: "pronto",
  falhou: "não ficou pronto",
};

/**
 * A média medida por imagem, em milissegundos.
 *
 * Medido em produção em 2026-09-12: três pedidos seguidos pelo HuggingFace com FLUX.1-schnell,
 * entre 4,6 e 5,2 segundos. Uso 5.000 ms, que é o meio da faixa.
 *
 * ESTE NÚMERO VAI ENVELHECER, e é bom que envelheça: se o provedor mudar, a estimativa fica
 * errada e a barra passa a chegar em 95% e esperar. Isso é visível — e é melhor do que uma
 * estimativa que se ajusta em silêncio e esconde que o provedor ficou lento.
 */
export const MEDIA_POR_IMAGEM_MS = 5000;

/**
 * T29.8 · E ELE ENVELHECEU, como o comentário acima previa. O relatório de 14/10: «uma barra com
 * "6%, 0 de 9 quadros prontos, já passou do tempo médio, e continua" (em 6% já tinha passado da
 * média)». A conta fecha: com a média em 5 s, meio quadro de nove é 6%, e 5 s depois o primeiro
 * quadro já «passou da média». Mas o desenho de hoje (o que mantém o personagem, com referência)
 * leva bem mais: nove quadros e duas fichas de personagem levaram 4 minutos na mesma rodada.
 *
 * Então a média passa a ser a MEDIDA NESTA GERAÇÃO — a média dos quadros que já ficaram prontos
 * — e, antes do primeiro, este padrão, tirado daquela rodada: 240 s para 9 quadros e 2 fichas
 * (cada ficha são dois pedidos: desenhar e recortar), ~18 s por pedido; arredondado para 20 s.
 */
export const MEDIA_DA_TIRINHA_MS = 20000;

/** A média medida até aqui, ou o padrão quando nenhum quadro terminou. */
export function mediaMedida(duracoesMs: number[], padrao = MEDIA_DA_TIRINHA_MS): number {
  const validas = duracoesMs.filter((d) => d > 0);
  if (!validas.length) return padrao;
  return validas.reduce((a, b) => a + b, 0) / validas.length;
}

/**
 * O avanço dentro do quadro atual, de 0 a 1, pelo tempo decorrido.
 *
 * O teto NUNCA é 1: a barra não completa o quadro atual por estimativa. Quem completa é o
 * fato — a imagem chegando. Uma barra que chega a 100% e continua girando é a coisa que mais
 * destrói confiança numa barra de progresso, e é exatamente o "fica batendo F5" do T2.87 com
 * outra roupa.
 *
 * ── E O TETO ERA 0,95, QUE COM POUCOS QUADROS MENTIA (T9.19) ────────────────────────────────
 *
 * Medido pelo QA em 02/10/2026, em 2 de 2 gerações de três cenas: **a tela dizia 98% com dois
 * quadros prontos de três**. A conta fecha: 2/3 de fato, mais 0,95 de um terço de estimativa,
 * dá 0,983. Noventa e oito por cento com um terço do trabalho por fazer, e a barra cheia.
 *
 * O defeito não era a estimativa existir — ele autorizou a estimativa (*"mesmo que essa
 * porcentagem seja meio fake, só pra gente ter uma noção do processo"*). Era ela valer quase um
 * quadro inteiro: com três quadros, um quadro é 33 pontos de barra, e emprestar 31 deles a uma
 * imagem que ainda não chegou faz a barra dizer o contrário da contagem que está do lado dela.
 *
 * Com o teto em **meio quadro**, a propriedade passa a ser dizível e medível: *a barra nunca vai
 * além de meio quadro à frente da contagem real*. Nos mesmos 2 de 3, ela diz 83% em vez de 98%,
 * e a contagem ao lado continua sendo o fato.
 */
export const TETO_DA_ESTIMATIVA = 0.5;

export function fracaoEstimada(decorridoMs: number, mediaMs = MEDIA_POR_IMAGEM_MS): number {
  if (decorridoMs <= 0) return 0;
  return Math.min(decorridoMs / mediaMs, TETO_DA_ESTIMATIVA);
}

/**
 * A porcentagem total: a parte REAL mais a parte ESTIMADA do quadro atual.
 *
 * Com 6 quadros e 2 prontos, a parte real é 33%. Se o terceiro está há 2,5 s em desenho, a
 * estimativa acrescenta metade de um sexto — cerca de 8%. Total 41%, e a tela diz que 33 é fato
 * e o resto é conta.
 */
export function porcentagem(feitos: number, total: number, fracaoDoAtual: number): number {
  if (total <= 0) return 0;
  const real = feitos / total;
  const estimado = fracaoDoAtual / total;
  return Math.round(Math.min(real + estimado, 0.99) * 100);
}

/**
 * O QUADRO ATUAL ESTÁ DEMORANDO MUITO MAIS DO QUE O NORMAL?
 *
 * ── O 39% DELE NÃO É APROXIMADO, É EXATO (`T5.3`) ────────────────────────────────────────────
 *
 * Ele, na letra: *"É uma travou em 39%, tá? Travou 100% aqui, parou (…) Morreu. Eu vou esperar um
 * pouquinho aqui, mas — enfim, travou, realmente."*
 *
 * A conta fecha sozinha. `fracaoEstimada` satura em `0.95` depois de 4,75 s, e ali ela para. Com
 * **cinco quadros, um pronto e o segundo preso**:
 *
 * ```
 * porcentagem(1, 5, 0.95)  =  round((1/5 + 0.95/5) * 100)  =  39
 * ```
 *
 * Trinta e nove por cento, na vírgula. E o que prendia o segundo quadro foi medido no mesmo dia
 * (`TS-T5.4.1`): a cascata gastava **95 segundos** por imagem — HuggingFace sem crédito (402),
 * Cloudflare recusando (400) e os 90 s do prazo do StableHorde antes de chegar ao Pollinations.
 *
 * **A barra não estava mentindo.** Ela estava presa porque o trabalho estava preso — e o comentário
 * do `MEDIA_POR_IMAGEM_MS` já tinha previsto exatamente isto: *"se o provedor mudar, a estimativa
 * fica errada e a barra passa a chegar em 95% e esperar. Isso é visível."*
 *
 * Era visível, e não era **legível**: uma barra parada em 39% sem dizer nada é indistinguível de
 * um produto morto, e foi assim que ele a leu. O `TS-T5.4.1` encurtou a espera de 95 s para 4 s; o
 * que falta, e é o que esta função existe para fazer, é a tela **contar** quando a espera for
 * anormal, em vez de deixar a pessoa decidir sozinha se desistiu.
 *
 * O limiar é **2,5×** a média: alto o bastante para não gritar a cada variação normal (as medições
 * ficaram entre 4,6 e 5,2 s), e baixo o bastante para falar antes dos 90 s de um prazo esgotado.
 */
export const VEZES_ATE_AVISAR = 2.5;

export function demorandoMais(decorridoMs: number, mediaMs = MEDIA_POR_IMAGEM_MS): boolean {
  return decorridoMs > mediaMs * VEZES_ATE_AVISAR;
}

/**
 * Quanto tempo falta, em segundos, pela mesma média. `null` quando não há o que estimar.
 *
 * ── A ESTIMATIVA VENCIDA NÃO VIRA ZERO, VIRA SILÊNCIO (T9.19) ───────────────────────────────
 *
 * Medido pelo QA em 02/10/2026: *"faltam cerca de 0 segundos"* ficou na tela **60 segundos sem
 * mudar um caractere**, e 57 na geração anterior. A causa era o `Math.max(…, 0)`: passado o tempo
 * médio, a parte do quadro atual virava zero, e com um quadro restante o total virava zero — um
 * número que a tela afirmava e que estava errado por um minuto.
 *
 * Zero é a pior resposta possível aqui, porque é a única que a pessoa pode conferir: ela espera
 * zero segundo e nada acontece. Então quando a média já passou, esta função não devolve número
 * nenhum — a tela diz uma frase, e quem conta a demora anormal é `demorandoMais`.
 */
export function faltamSegundos(
  feitos: number,
  total: number,
  decorridoNoAtualMs: number,
  mediaMs = MEDIA_POR_IMAGEM_MS
): number | null {
  const restantes = total - feitos;
  if (restantes <= 0) return null;
  if (decorridoNoAtualMs >= mediaMs) return null;
  const doAtual = mediaMs - decorridoNoAtualMs;
  const dosOutros = (restantes - 1) * mediaMs;
  return Math.ceil((doAtual + dosOutros) / 1000);
}

export interface PropsProgresso {
  /**
   * T29.8 · os prontos SAEM DOS QUADROS, e não de um contador à parte. O relatório de 14/10 viu a
   * fichinha 1 dizer «pronto» ao lado de «0 de 9 quadros prontos»: o contador só andava depois de
   * o quadro 1 virar a referência dos outros (mais um pedido, de segundos), e a fichinha já tinha
   * andado. Uma conta, dois lugares.
   */
  total: number;
  /** O estado de cada quadro, na ordem. É o que responde à segunda pergunta do T2.86. */
  quadros: EstadoDoQuadro[];
  /** Quando o quadro atual começou. `null` quando nada está sendo desenhado. */
  comecouEm: number | null;
  /** T29.8 · a média desta geração (ver `mediaMedida`). */
  mediaMs?: number;
  /**
   * T29.8 · o que está sendo feito ANTES dos quadros (as fichas dos personagens). Enquanto houver
   * fase, nenhum quadro está desenhando e a barra não estima nada: ela diz a fase.
   */
  fase?: string | null;
}

/** Quantos quadros já terminaram — prontos ou que não saíram. */
export const terminados = (quadros: EstadoDoQuadro[]) => quadros.filter((q) => q === "pronto" || q === "falhou").length;

function ProgressoDaGeracao({ total, quadros, comecouEm, mediaMs = MEDIA_DA_TIRINHA_MS, fase = null }: PropsProgresso) {
  const feitos = terminados(quadros);
  const prontos = quadros.filter((q) => q === "pronto").length;
  const atual = quadros.indexOf("desenhando") + 1;
  // O RELÓGIO QUE FAZ A BARRA ANDAR, e ele é o ponto do desenho.
  //
  // Sem ele, a barra só se moveria quando um quadro terminasse — de 5 em 5 segundos, em saltos.
  // E com total = 1, que era o caso dele, ela não se moveria NUNCA. "A gente não sabe se tá
  // gerando de fato" é literalmente a experiência de olhar uma barra parada.
  const [agora, setAgora] = useState(() => comecouEm ?? 0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (comecouEm === null) {
      if (timer.current) clearInterval(timer.current);
      return;
    }
    // 250 ms: rápido o bastante para a barra parecer viva, lento o bastante para não ser
    // trabalho inútil. Abaixo disso ninguém vê a diferença e o navegador re-renderiza de graça.
    setAgora(Date.now());
    timer.current = setInterval(() => setAgora(Date.now()), 250);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [comecouEm]);

  const decorrido = comecouEm === null ? 0 : Math.max(agora - comecouEm, 0);
  const fracao = comecouEm === null ? 0 : fracaoEstimada(decorrido, mediaMs);
  const pct = porcentagem(feitos, total, fracao);
  const faltam = comecouEm === null ? null : faltamSegundos(feitos, total, decorrido, mediaMs);
  const demorando = comecouEm !== null && demorandoMais(decorrido, mediaMs);
  const mediaEmSegundos = Math.round(mediaMs / 1000);

  return (
    <div className="prog-caixa">
      {/* O NÚMERO E A BARRA DIZEM A MESMA COISA POR DOIS CANAIS. `aria-live` anuncia sem que
          ninguém precise ficar olhando — e quem usa leitor de tela é justamente quem mais sofre
          com "a tela não diz nada". */}
      <div
        className="prog-barra"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progresso dos desenhos"
      >
        <span className="prog-trilho" style={{ width: `${pct}%` }} />
      </div>

      <p className="prog-linha" aria-live="polite">
        <strong>{pct}%</strong>
        {" — "}
        {prontos} de {total} {total === 1 ? "quadro pronto" : "quadros prontos"}
        {feitos > prontos && ` (${feitos - prontos} não ${feitos - prontos === 1 ? "saiu" : "saíram"})`}
        {fase && (
          <>
            {" · "}
            <span className="prog-estimativa">{fase}</span>
          </>
        )}
        {faltam !== null && (
          <>
            {" · "}
            <span className="prog-estimativa">
              faltam cerca de {faltam} {faltam === 1 ? "segundo" : "segundos"}
            </span>
          </>
        )}
        {/* T9.19 · A FRASE SEM NÚMERO. Quando a média já passou, não há número honesto a
            dizer — e dizer "0 segundos" é a única resposta que a pessoa pode conferir e ver
            que está errada. A frase diz o que se sabe: passou da média, e continua. */}
        {/* T29.8 · e ela fala do QUADRO, não da geração: «já passou do tempo médio» ao lado de 6%
            lia como se a história inteira estivesse atrasada, quando era o quadro 1 que passava
            dos 5 s de uma média velha. */}
        {faltam === null && comecouEm !== null && feitos < total && (
          <>
            {" · "}
            <span className="prog-estimativa">
              o quadro {atual || feitos + 1} está levando mais que os {mediaEmSegundos} s de costume, e continua
            </span>
          </>
        )}
      </p>

      {/* A HONESTIDADE SOBRE O NÚMERO — dúvida D6.
          "Mesmo que essa porcentagem seja meio fake, só pra gente ter uma noção do processo"
          (T2.88). Ele autorizou a estimativa; dizer que ela é estimativa é o que faz a
          autorização valer sem virar mentira. */}
      <p className="prog-nota">
        Os quadros prontos são contagem real. O tempo que falta é estimativa, pela média dos
        quadros desta história (cerca de {mediaEmSegundos} s cada).
      </p>

      {/* ── A BARRA QUE PARA TEM QUE DIZER QUE PAROU (T5.3) ──────────────────────────────────
          Sem isto, uma barra presa em 39% é indistinguível de um produto morto — e foi assim que
          ele a leu: "travou, realmente". O texto diz três coisas, nessa ordem: que estamos vendo
          a demora, que o trabalho NÃO parou, e o que ele pode fazer. Nenhuma delas é "tente de
          novo": isto aqui não é falha, é fila. */}
      {demorando && (
        <p className="prog-demora" role="status">
          Este quadro está demorando mais que o normal — o serviço de desenho deve estar cheio.
          <strong> Ele não parou</strong>, e os quadros que já ficaram prontos estão guardados.
          Você pode esperar, ou seguir montando os outros no editor.
        </p>
      )}

      {/* ── A SEGUNDA PERGUNTA DO T2.86 ────────────────────────────────────────────────────
          "já tem uma página aqui gerada abaixo, NÃO SEI SE ELA VAI SER INCREMENTADA E ALTERADA."

          Não é "está rodando?" — é "isto que estou vendo é o final?". Um contador não responde.
          Uma lista com o estado de cada quadro responde: o que está pronto não muda mais, o que
          está na fila ainda vai aparecer. */}
      <ol className="prog-quadros" aria-label="Estado de cada quadro">
        {quadros.map((e, i) => (
          <li key={i} className={`prog-quadro prog-${e}`}>
            <span className="prog-quadro-num" aria-hidden="true">
              {i + 1}
            </span>
            <span className="prog-quadro-estado">{ROTULO_DO_QUADRO[e]}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default ProgressoDaGeracao;
